import Vuex from "vuex";
import Vue from "vue";
import { graphqlClient } from "@/utils/graphql";
import gql from "graphql-tag";
import { MUTATION_ADD_REPORT } from "@/models/mutations/AddReportMutation";
import { MUTATION_EDIT_REPORT } from "@/models/mutations/EditReportMutation";
import { QUERY_REPORT_OVERVIEW } from "@/models/queries/ReportOverviewQuery";
import { QUERY_REPORT_SEARCH } from "@/models/queries/ReportSearchQuery";
import { QUERY_REPORT_SEARCH_CRITERIA } from "@/models/queries/ReportSearchCriteriaQuery";
import { QUERY_EXPORT_REPORTS } from "@/models/queries/ExportReportsQuery";
import { QUERY_CHECK_REPORT_NUMBER } from "@/models/queries/CheckReportNumberQuery";
import { QUERY_REPORT_VIEW } from "@/models/queries/ReportViewQuery";
import { REPORT_DETAIL_QUERY } from "@/models/queries/ReportDetailQuery";
import { REPORT_DETAIL_SECRET_QUERY } from "@/models/queries/ReportDetailSecretQuery";
import { REPORT_PRINT_QUERY } from "@/models/queries/ReportPrintQuery";
import { DELETE_REPORT_MUTUATION } from "@/models/mutations/DeleteReportMutation";
import { CHANGE_REPORT_STEP_MUTATION } from "@/models/mutations/ChangeReportStepMutation";
import { DELETE_ATTACHMENT_MUTATION } from "@/models/mutations/DeleteAttachmentMutation";
import moment from "moment";
import { SET_REPORT_DATE_MUTATION } from "@/models/mutations/SetReportDate";
import { SetReportDate, SetReportDateVariables } from "@/models/mutations/types/SetReportDate";
import { SetReportDateType, ReportStep } from "@/models/graphql-global-types";
import { DeleteSheetFromReport, DeleteSheetFromReportVariables } from "@/models/mutations/types/DeleteSheetFromReport";
import { DELETE_SHEET_FROM_REPORT_MUTATION } from "@/models/mutations/DeleteSheetFromReport";
import lodash from "lodash";
import { PrintReport, PrintReportVariables } from "@/models/queries/types/PrintReport";
import { REPORT_PRINT_PDF_QUERY } from "@/models/queries/ReportPrintPDFQuery";

Vue.use(Vuex);

function getNewApiByGuessing() {
  const hostname = window.location.hostname;
  if (hostname.endsWith(".herokuapp.com")) {
    if (hostname.includes("avvio-cedoon-portal-dev")) {
      return "https://dev-cedoon-api.herokuapp.com";
    } else if (hostname.includes("avvio-cedoon-portal-sta")) {
      return "https://sta-cedoon-api.herokuapp.com";
    } else if (hostname.includes("avvio-cedoon-portal.herokuapp.com")) {
      return "https://cedoon-api.herokuapp.com";
    }
  }
  return "http://localhost:3000";
}

const reportStore = {
  namespaced: true,
  state: {
    reports: [],
    reportFilterStep: ReportStep.IN_PROGRESS,
  },
  mutations: {
    FETCH(state, reports) {
      state.reports = reports;
    },
  },
  actions: {
    fetchSearch({}, parameter) {
      return graphqlClient.query({
        query: gql`
          ${QUERY_REPORT_SEARCH}
        `,
        variables: parameter,
      });
    },
    fetchReportView({}, parameter) {
      return graphqlClient.query({
        query: gql`
          ${QUERY_REPORT_VIEW}
        `,
        variables: parameter,
      });
    },
    fetchSearchCriteria({}, parameter) {
      return graphqlClient.query({
        query: gql`
          ${QUERY_REPORT_SEARCH_CRITERIA}
        `,
        variables: parameter,
      });
    },
    save({}, report, reportId) {
      const variables: any = {
        reportNumber: report.report_number,
        name: report.name,
        orderConfirmation: report.order_confirmation,
        customerOrderNumber: report.customer_order_number,
        shippingDate: moment(report.shipping_date).format(),
        orderDate: report.order_date ? moment(report.order_date).format() : undefined,
        customer: report.customer,
        supplier: report.manufacturer,
        productionPlant: report.production_plant,
        work_order_nr: report.work_order_nr,
        products: report.products.map((product, index) => {
          let measurements: any[] | undefined;

          if (product.measurements) {
            measurements = product.measurements
              .filter((m) => m.existsOnTemplate)
              .map((m) => {
                return {
                  characteristic: `${m.id}`,
                  values: m.values,
                };
              });

            measurements = measurements && measurements.length > 0 ? measurements : undefined;
          }

          const p: any = {
            id: `${product.id}`,
            materialCertificate: product.material_certificate,
            heatNr: product.heat_nr,
            batch: product.charge,
            selectedTemplate: { id: `${product.selectedTemplate.id}` },
            measurements: measurements,
            orderQuantity:
              product.orderQuantity && product.orderQuantity.toString().trim().length > 0
                ? parseInt(product.orderQuantity, 10)
                : undefined,
            woodInfo: product.woodInfo,
            work_order_nr: product.work_order_nr,
            existed: report.id ? !!product.existed : undefined,
            sheetId: report.id ? product.sheetId : undefined,
          };

          return p;
        }),
      };

      let mutation = MUTATION_ADD_REPORT;

      if (report.id) {
        mutation = MUTATION_EDIT_REPORT;
        variables.reportId = `${report.id}`;
      }

      return new Promise((resolve, reject) => {
        graphqlClient
          .mutate({
            mutation,
            variables: variables,
          })
          .then((response) => {
            resolve();
          })
          .catch((e) => reject(e));
      });
    },
    detail({}, id) {
      return graphqlClient.query({
        query: REPORT_DETAIL_QUERY,
        variables: { reportId: id },
      });
    },
    detailWithSecret({}, secret) {
      return graphqlClient.query({
        query: REPORT_DETAIL_SECRET_QUERY,
        variables: { secret: secret },
      });
    },
    deleteAttachment({}, id) {
      return graphqlClient.mutate({
        mutation: gql`
          ${DELETE_ATTACHMENT_MUTATION}
        `,
        variables: { uuid: id },
      });
    },
    getReportExport({}, ids) {
      return graphqlClient.query({
        query: gql`
          ${QUERY_EXPORT_REPORTS}
        `,
        variables: { reportIds: ids.map((id) => `${id}`) },
      });
    },
    deleteSheetFromReport({}, { reportId, sheetId }) {
      return graphqlClient.mutate<DeleteSheetFromReport, DeleteSheetFromReportVariables>({
        mutation: DELETE_SHEET_FROM_REPORT_MUTATION,
        variables: { reportId, sheetId },
      });
    },
  },
};

export default reportStore;
